import React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import { TranslateFunction } from 'react-localize-redux';

import {
  getDateFromMsDate,
  getRelativeDate,
} from '../../../utils/dateTimeUtils';
import { Button, Typography } from '../../atoms';
import { Dialog } from '../../layouts';

const useStyles = makeStyles((theme: Theme) => ({
  boldContent: {
    fontWeight: theme.typography.fontWeightBold,
  },
  codeContainer: {
    marginBottom: theme.spacing(0.5),
    marginTop: theme.spacing(2.5),
  },
  container: {
    marginBottom: theme.spacing(2),
  },
  details: {
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

export type LoyaltyVoucherDialogProps = {
  details: string;
  expiry: string;
  isApplyVoucherLoading?: boolean;
  languageCode: string;
  onApplyVoucher?: (voucherCode: string) => void;
  onClose: () => void;
  open: boolean;
  translate: TranslateFunction;
  voucherCode: string;
};

export function LoyaltyVoucherDialog(
  props: LoyaltyVoucherDialogProps,
): JSX.Element {
  const {
    details,
    expiry,
    isApplyVoucherLoading,
    languageCode,
    onApplyVoucher,
    onClose,
    open,
    translate,
    voucherCode,
  } = props;
  const classes = useStyles();

  const expiryDate = getDateFromMsDate(expiry);
  const relativeExpiryDate = getRelativeDate(
    expiryDate,
    translate,
    languageCode,
  );

  const getDialogActions = (): JSX.Element => {
    if (onApplyVoucher) {
      return (
        <Button
          data-fd="apply-voucher-dialog-button"
          fullWidth
          status={isApplyVoucherLoading ? 'loading' : 'default'}
          onClick={(): void => onApplyVoucher(voucherCode)}
        >
          {translate('Loyalty_applyVoucher')}
        </Button>
      );
    }
    return null;
  };

  return (
    <Dialog
      actions={getDialogActions()}
      dialogName="loyalty-voucher-details"
      onTitleClick={onClose}
      open={open}
    >
      <Typography variant="h1">
        {translate('Loyalty_loyaltyRewardTitle')}
      </Typography>
      <div className={classes.codeContainer}>
        <Typography variant="body2">
          {translate('LoyaltyVoucherDialog_voucherCode')}
        </Typography>
        <Typography className={classes.boldContent} variant="body1">
          {voucherCode}
        </Typography>
      </div>
      <div className={classes.container}>
        <Typography variant="body2">
          {translate('LoyaltyVoucherDialog_details')}
        </Typography>
        <Typography className={classes.details} variant="body2-secondary">
          {details}
        </Typography>
      </div>
      <div className={classes.container}>
        <Typography variant="body2">
          {translate('LoyaltyVoucherDialog_expires')}
        </Typography>
        <Typography className={classes.boldContent} variant="body1">
          {relativeExpiryDate}
        </Typography>
      </div>
    </Dialog>
  );
}
